/**
 * Plain server errors everywhere (ADR-0065): a plain title for the kind of problem, a plain line
 * for each detail the console recognises (policy reasons through `reasons.ts`, common service
 * messages through `ERROR_TEXT_RULES`), what to do next, and the service's own words tucked
 * into a collapsed "Technical details".
 */
import { explainError, type ExplainedDetail, type PlainError } from "@ap/api";
import { fieldLabel } from "@ap/ui-kit";
import { Alert, Collapse, Typography } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** One detail as a plain sentence (falls back to "The system said: …"). */
export function detailText(t: TFn, d: ExplainedDetail): string {
  if (d.key) {
    const text = t(`error.detail.${d.key}`, { ns: "pickers", ...d.values });
    return d.values.at ? `${fieldLabel(d.values.at)}: ${text}` : text;
  }
  if (d.reasonKey) return `${t(`reason.${d.reasonKey}.why`, { ns: "clarity", ...d.values })} ${t(`reason.${d.reasonKey}.next`, { ns: "clarity", ...d.values })}`;
  return t("error.raw", { ns: "pickers", raw: d.raw });
}

/** Title + next step for the kind of error. */
export function kindText(t: TFn, e: PlainError): { title: string; next: string } {
  return { title: t(`error.kind.${e.kind}.title`, { ns: "pickers" }), next: t(`error.kind.${e.kind}.next`, { ns: "pickers" }) };
}

/** A short plain message for toasts: the first explained detail, else the kind's title. */
export function plainErrorMessage(t: TFn, err: unknown): string {
  const e = explainError(err);
  const known = e.details.find((d) => d.key || d.reasonKey);
  return known ? detailText(t, known) : kindText(t, e).title;
}

export function ServerErrorAlert({ error, title, action, testId = "server-error", style }: { error: unknown; title?: ReactNode; action?: ReactNode; testId?: string; style?: React.CSSProperties }) {
  const { t } = useTranslation("pickers");
  const e = explainError(error);
  const kind = kindText(t, e);
  const explained = e.details.filter((d) => d.key || d.reasonKey);
  const unknown = e.details.filter((d) => !d.key && !d.reasonKey);
  return (
    <Alert
      type="error"
      showIcon
      style={style}
      data-testid={testId}
      data-error-kind={e.kind}
      action={action}
      message={title ?? kind.title}
      description={
        <div>
          {explained.length ? (
            <ul style={{ margin: 0, paddingInlineStart: 18 }} data-testid="error-details">
              {explained.map((d) => (
                <li key={d.raw} dir="auto">
                  {detailText(t, d)}
                </li>
              ))}
            </ul>
          ) : null}
          {!explained.length || e.kind === "unavailable" || e.kind === "offline" || e.kind === "server" ? (
            <Typography.Paragraph style={{ margin: 0 }} type="secondary">
              {kind.next}
            </Typography.Paragraph>
          ) : null}
          {e.raw || unknown.length ? (
            <Collapse
              ghost
              size="small"
              style={{ marginBlockStart: 4 }}
              items={[
                {
                  key: "tech",
                  label: t("tech.title", { ns: "clarity" }),
                  children: (
                    <code className="ap-mono" dir="ltr" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} data-testid="error-raw">
                      {e.status ? `HTTP ${e.status} · ` : ""}
                      {e.raw}
                    </code>
                  ),
                },
              ]}
            />
          ) : null}
        </div>
      }
    />
  );
}
